import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Loader2, TrendingUp, ArrowLeft, Mail, UserPlus, Sparkles, Clock, DollarSign, Target, ChevronDown } from "lucide-react";

const RANGE_OPTIONS = [
  { days: 7, label: "Last 7 days" },
  { days: 30, label: "Last 30 days" },
  { days: 90, label: "Last 90 days" },
  { days: 365, label: "Last year" },
];

const STATUS_FILTER_OPTIONS = [
  { value: "", label: "All" },
  { value: "free_claimed", label: "Still on Claim Plan" },
  { value: "trialing", label: "On trial" },
  { value: "active", label: "Paying" },
  { value: "canceled", label: "Canceled" },
];

function fmtTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso; }
}

export default function AdminConversion() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [days, setDays] = useState(30);
  const [statusFilter, setStatusFilter] = useState("");
  const [overview, setOverview] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || user.role !== "platform_admin") {
      nav("/login");
      return;
    }
    setLoading(true);
    Promise.all([
      api.get(`/admin/conversion/overview?days=${days}`),
      api.get(`/admin/conversion/free-claimed-companies?days=${Math.max(days, 90)}${statusFilter ? `&status=${statusFilter}` : ""}`),
    ]).then(([o, c]) => {
      setOverview(o.data);
      setCompanies(c.data?.companies || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [days, statusFilter, user, nav]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FBFAF6] flex items-center justify-center" data-testid="conversion-loading">
        <div className="text-slate-500 inline-flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Crunching numbers…
        </div>
      </div>
    );
  }

  const f = overview?.funnel || {};
  const r = overview?.rates || {};

  return (
    <div className="min-h-screen bg-[#FBFAF6]" data-testid="conversion-page">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-slate-500 hover:text-slate-700">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="w-10 h-10 rounded-xl bg-[#C75D33]/10 text-[#C75D33] flex items-center justify-center">
              <TrendingUp className="w-5 h-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-bold text-slate-900">Conversion tracking</h1>
              <p className="text-xs text-slate-500">Cold outreach → free claim → paying customer</p>
            </div>
          </div>
          <div className="relative">
            <select
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value))}
              className="appearance-none pl-3 pr-9 py-2 rounded-lg border border-slate-300 bg-white text-sm font-semibold cursor-pointer focus:outline-none focus:border-[#C75D33]"
              data-testid="conversion-range-select"
            >
              {RANGE_OPTIONS.map((o) => <option key={o.days} value={o.days}>{o.label}</option>)}
            </select>
            <ChevronDown className="w-4 h-4 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400" />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Funnel headline */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="conversion-headline-stats">
          <StatCard icon={Mail}     label="Invites sent"        value={f.invites_sent}        sub={f.invites_pending ? `${f.invites_pending} pending` : null} tone="slate" />
          <StatCard icon={UserPlus} label="Free claim signups"  value={f.free_claim_signups}  sub={f.invites_sent ? `${r.invite_claim_rate_pct}% of invites` : null} tone="copper" />
          <StatCard icon={Sparkles} label="Upgraded to trial"   value={f.upgraded_to_trial}   sub={f.free_claim_signups ? `${r.free_to_trial_rate_pct}% of free` : null} tone="emerald" />
          <StatCard icon={DollarSign} label="Paid subscriptions" value={f.paid_subscriptions} sub={f.total_signups ? `${r.signup_to_paid_rate_pct}% of all signups` : null} tone="emerald" />
        </div>

        {/* Funnel visualization */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-6" data-testid="conversion-funnel-card">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-4">Funnel · {RANGE_OPTIONS.find((o) => o.days === days)?.label.toLowerCase()}</div>
          <FunnelRow label="Invites sent"             value={f.invites_sent}        max={f.invites_sent || 1} pct={100} tone="slate" testid="funnel-step-invites" />
          <FunnelRow label="Claimed (free signup)"    value={f.invites_claimed}     max={f.invites_sent || 1} pct={r.invite_claim_rate_pct}    tone="copper"  testid="funnel-step-claimed" />
          <FunnelRow label="Upgraded to trial"        value={f.upgraded_to_trial}   max={f.free_claim_signups || 1} pct={r.free_to_trial_rate_pct} tone="emerald" testid="funnel-step-upgraded" />
          {!!f.direct_trial_signups && (
            <div className="mt-4 pt-4 border-t border-slate-100 text-xs text-slate-500">
              <Target className="w-3.5 h-3.5 inline -mt-0.5 mr-1" />
              <span className="font-semibold text-slate-700">{f.direct_trial_signups}</span> additional signups went straight to trial (no free-claim phase).
            </div>
          )}
        </div>

        {/* Free-claim cohort list */}
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between gap-3">
            <div>
              <h2 className="font-display text-base font-bold text-slate-900">Free claim cohort</h2>
              <p className="text-xs text-slate-500 mt-0.5">Showing companies that started on the Claim Plan ({Math.max(days, 90)} days)</p>
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-1.5 rounded-lg border border-slate-300 bg-white text-xs font-semibold cursor-pointer focus:outline-none"
              data-testid="conversion-status-filter"
            >
              {STATUS_FILTER_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          {companies.length === 0 ? (
            <div className="px-5 py-12 text-center text-sm text-slate-500" data-testid="conversion-empty">
              No companies match yet. Once you start sending invites, claimed signups will show up here.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="conversion-cohort-table">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50">
                    <th className="px-4 py-3 text-left font-bold">Company</th>
                    <th className="px-4 py-3 text-left font-bold">Email</th>
                    <th className="px-4 py-3 text-left font-bold">Claimed</th>
                    <th className="px-4 py-3 text-left font-bold">Status</th>
                    <th className="px-4 py-3 text-right font-bold">Time to upgrade</th>
                  </tr>
                </thead>
                <tbody>
                  {companies.map((c) => (
                    <tr key={c.id} className="border-t border-slate-100" data-testid={`conversion-row-${c.slug}`}>
                      <td className="px-4 py-3 font-semibold text-slate-900">{c.business_name || c.slug}</td>
                      <td className="px-4 py-3 text-slate-600">{c.owner_email}</td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmtTime(c.free_claimed_at)}</td>
                      <td className="px-4 py-3"><StatusPill status={c.subscription_status} /></td>
                      <td className="px-4 py-3 text-right tabular-nums text-slate-700 whitespace-nowrap">
                        {c.time_to_upgrade_hours == null ? <span className="text-slate-400">—</span> : (
                          <span className="inline-flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5 text-slate-400" />
                            {c.time_to_upgrade_hours < 1 ? "<1h" : c.time_to_upgrade_hours < 48 ? `${Math.round(c.time_to_upgrade_hours)}h` : `${Math.round(c.time_to_upgrade_hours / 24)}d`}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, tone }) {
  const tones = {
    slate:   { ring: "border-slate-200",   icon: "bg-slate-100 text-slate-700" },
    copper:  { ring: "border-[#C75D33]/30", icon: "bg-[#C75D33]/10 text-[#C75D33]" },
    emerald: { ring: "border-emerald-200", icon: "bg-emerald-50 text-emerald-700" },
  };
  const t = tones[tone] || tones.slate;
  return (
    <div className={`bg-white border ${t.ring} rounded-2xl p-4`}>
      <div className="flex items-start gap-2.5">
        <div className={`w-8 h-8 shrink-0 rounded-lg ${t.icon} flex items-center justify-center`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</div>
          <div className="font-display text-2xl font-bold text-slate-900 mt-0.5">{value ?? 0}</div>
          {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
        </div>
      </div>
    </div>
  );
}

function FunnelRow({ label, value, max, pct, tone, testid }) {
  const tones = {
    slate:   "bg-slate-200",
    copper:  "bg-[#C75D33]",
    emerald: "bg-emerald-500",
  };
  const widthPct = max > 0 ? Math.max((value / max) * 100, value > 0 ? 4 : 0) : 0;
  return (
    <div className="mb-3 last:mb-0" data-testid={testid}>
      <div className="flex items-baseline justify-between mb-1.5">
        <div className="text-sm font-semibold text-slate-900">{label}</div>
        <div className="text-sm text-slate-600 tabular-nums">
          <span className="font-bold text-slate-900">{value ?? 0}</span>
          {typeof pct === "number" && pct > 0 && pct < 100 && <span className="text-xs ml-1.5">· {pct}%</span>}
        </div>
      </div>
      <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full ${tones[tone] || tones.slate} rounded-full transition-all`} style={{ width: `${widthPct}%` }} />
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    free_claimed: { label: "Claim Plan",  tone: "bg-[#C75D33]/10 text-[#C75D33] border-[#C75D33]/30" },
    trialing:     { label: "On trial",    tone: "bg-amber-50 text-amber-700 border-amber-200" },
    active:       { label: "Paying",      tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    canceled:     { label: "Canceled",    tone: "bg-rose-50 text-rose-700 border-rose-200" },
    past_due:     { label: "Past due",    tone: "bg-rose-50 text-rose-700 border-rose-200" },
    inactive:     { label: "Inactive",    tone: "bg-slate-100 text-slate-600 border-slate-200" },
  };
  const m = map[status] || { label: status || "?", tone: "bg-slate-100 text-slate-600 border-slate-200" };
  return <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wider ${m.tone}`}>{m.label}</span>;
}
